import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  MessageCircle,
  Award,
  Users,
  Calendar,
  Target,
  Heart,
  FileBarChart,
  GraduationCap,
  Sparkles,
  Star,
  Globe,
  Search,
  Megaphone,
} from "lucide-react";
import { SITE, waUrl } from "../lib/site";
import { Section, Eyebrow, SectionHeading } from "../components/SectionPrimitives";
import CTABand from "../components/CTABand";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const numbers = [
  { value: "+10", label: "Anos de Mercado", color: "#4285F4" },
  { value: "+100", label: "Clientes Ativos", color: "#EA4335" },
  { value: "+R$ 5MM", label: "Em Investimentos Gerenciados", color: "#FBBC05" },
  { value: "+1.000", label: "Leads Gerados Mensalmente", color: "#34A853" },
];

const services = [
  {
    title: "Desenvolvimento de Sites",
    desc: "Criamos sites profissionais, rápidos e preparados para converter visitantes em clientes.",
    icon: Globe,
    color: "#4285F4",
    topClass: "og-card-top-blue",
    href: "/criacao-de-sites",
  },
  {
    title: "Google Ads",
    desc: "Colocamos sua empresa nas primeiras posições do Google para quem procura exatamente o que você vende.",
    icon: Search,
    color: "#EA4335",
    topClass: "og-card-top-red",
    href: "/google-ads",
  },
  {
    title: "Meta Ads",
    desc: "Gerenciamos campanhas estratégicas no Facebook e Instagram para aumentar visibilidade e vendas.",
    icon: Megaphone,
    color: "#34A853",
    topClass: "og-card-top-green",
    href: "/meta-ads",
  },
];

const differentials = [
  {
    icon: Sparkles,
    title: "Estratégias Personalizadas",
    desc: "Cada negócio possui necessidades diferentes. Desenvolvemos estratégias exclusivas para cada cliente.",
    color: "#4285F4",
  },
  {
    icon: Target,
    title: "Foco em Resultado",
    desc: "Nosso objetivo é gerar crescimento real, mensurável através de métricas e dados.",
    color: "#EA4335",
  },
  {
    icon: Heart,
    title: "Atendimento Humanizado",
    desc: "Suporte próximo, rápido e transparente — você sempre falará com quem cuida da sua conta.",
    color: "#FBBC05",
  },
  {
    icon: FileBarChart,
    title: "Relatórios Completos",
    desc: "Você acompanha tudo o que está acontecendo com seus investimentos em tempo real.",
    color: "#34A853",
  },
  {
    icon: GraduationCap,
    title: "Equipe Especializada",
    desc: "Profissionais atualizados com as principais tendências do mercado digital.",
    color: "#4285F4",
  },
  {
    icon: Award,
    title: "Google Partner",
    desc: "Reconhecimento oficial do Google pela qualidade da gestão das nossas campanhas.",
    color: "#EA4335",
  },
];

const homeFaqs = [
  {
    q: "Quanto preciso investir em Google Ads ou Meta Ads para ter resultado?",
    a: "Não existe um valor mínimo universal — depende do seu segmento, região e nível de concorrência. Após uma análise gratuita, recomendamos o orçamento ideal para que sua campanha gere retorno saudável. Trabalhamos com clientes que investem desde R$ 500 até dezenas de milhares por mês.",
  },
  {
    q: "Em quanto tempo eu começo a ver resultados?",
    a: "Campanhas de Google Ads costumam gerar os primeiros contatos nas primeiras 48 a 72 horas após a ativação. Já o Meta Ads começa de imediato, e o SEO/site leva entre 60 e 90 dias para amadurecer. Os 90 primeiros dias são essenciais para otimizações que aumentam o retorno.",
  },
  {
    q: "Vocês têm fidelidade ou contrato longo?",
    a: "Não. Acreditamos que o resultado é quem fideliza. Nossos contratos são mensais, transparentes e sem multas escondidas. Mais de 100 clientes ativos continuam com a O.G. porque entregamos resultado consistente mês após mês.",
  },
  {
    q: "Quem é o dono do site, do domínio e das contas de anúncio?",
    a: "Você. Sempre. Todas as contas (Google Ads, Meta Business, Google Analytics) ficam no seu nome e CNPJ, e você tem acesso total às credenciais. O domínio e o site são propriedade do cliente desde o dia da entrega.",
  },
  {
    q: "Como funciona o acompanhamento dos resultados?",
    a: "Você recebe relatórios mensais detalhados (PDF + reunião online opcional), além de acesso a um dashboard com métricas em tempo real — cliques, leads, custo por conversão, ROAS. Tudo transparente, nada de números maquiados.",
  },
  {
    q: "Vocês atendem qualquer cidade do Brasil?",
    a: "Sim. Atendemos clientes em todas as regiões do país. Nosso processo é 100% remoto, com reuniões online, suporte direto via WhatsApp e relatórios digitais. Temos clientes do Sul ao Nordeste.",
  },
  {
    q: "O que diferencia a O.G. de outras agências?",
    a: "Somos Google Partner certificados, com mais de 10 anos de mercado e mais de R$ 5 milhões já gerenciados em campanhas. Mas o que mais nos diferencia é o atendimento humano e estratégico — você fala diretamente com quem cuida da sua conta, sem terceirizações.",
  },
];


const testimonials = [
  {
    name: "Carlos Henrique",
    role: "Empresário — Construção Civil",
    text: "Aumentamos significativamente nossos contatos através do Google. Excelente atendimento, equipe muito atenciosa.",
  },
  {
    name: "Fernanda Souza",
    role: "Diretora — Clínica Odontológica",
    text: "Profissionais extremamente competentes. Nosso site ficou incrível e os agendamentos aumentaram em 3x.",
  },
  {
    name: "Rafael Oliveira",
    role: "Sócio — Escritório de Advocacia",
    text: "Resultado acima das expectativas. As campanhas de Google Ads trouxeram clientes qualificados todos os dias. Recomendo.",
  },
  {
    name: "Juliana Martins",
    role: "Proprietária — Imobiliária",
    text: "Equipe comprometida e transparente. Sempre mostrando melhorias e resultados — sentimos parceria de verdade.",
  },
];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO — Dark bento style */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden bg-[#000000] text-white"
      >
        {/* Decorative ambient blobs */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-32 -left-40 w-[420px] h-[420px] rounded-full opacity-[0.12] blur-3xl"
          style={{ background: "#4285F4" }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute bottom-0 right-0 w-[520px] h-[520px] rounded-full opacity-[0.10] blur-3xl"
          style={{ background: "#34A853" }}
        />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-20 sm:pt-20 sm:pb-28 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* LEFT */}
          <div className="lg:col-span-7 reveal">
            <span
              data-testid="hero-eyebrow-badge"
              className="inline-flex items-center gap-2 border border-white/20 bg-white/[0.04] backdrop-blur-sm px-4 py-2 rounded-full text-[11px] tracking-[0.25em] uppercase text-white/90"
            >
              <span className="flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#4285F4]" />
                <span className="w-1.5 h-1.5 rounded-full bg-[#EA4335]" />
                <span className="w-1.5 h-1.5 rounded-full bg-[#FBBC05]" />
                <span className="w-1.5 h-1.5 rounded-full bg-[#34A853]" />
              </span>
              Marketing & Performance
            </span>

            <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tighter text-white leading-[1.02]">
              Transformamos{" "}
              <span className="text-[#4285F4]">cliques</span> em clientes e{" "}
              <span className="text-[#34A853]">investimento</span> em
              crescimento.
            </h1>

            <p className="mt-8 text-base sm:text-lg text-white/65 leading-relaxed max-w-xl">
              Somos especialistas em geração de leads, vendas e crescimento
              empresarial através de{" "}
              <strong className="text-white font-semibold">Google Ads</strong>,{" "}
              <strong className="text-white font-semibold">Meta Ads</strong> e{" "}
              <strong className="text-white font-semibold">
                sites de alta conversão
              </strong>
              . Há mais de 10 anos.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href={waUrl("Olá! Quero falar com a equipe da O.G. Digital.")}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-whatsapp-cta"
                className="inline-flex items-center justify-center gap-2 bg-[#34A853] text-white px-7 py-4 font-medium hover:bg-[#2c8f47] transition rounded-md"
              >
                <MessageCircle size={18} />
                Falar no WhatsApp
              </a>
              <Link
                to="/contato"
                data-testid="hero-orcamento-cta"
                className="inline-flex items-center justify-center gap-2 border border-white/40 text-white px-7 py-4 font-medium hover:bg-white hover:text-[#000000] transition rounded-md"
              >
                Solicitar Diagnóstico Gratuito
                <ArrowUpRight size={18} />
              </Link>
            </div>

            {/* Google Partner inline card */}
            <div className="mt-12 max-w-xl border border-white/10 bg-white/[0.04] backdrop-blur-sm rounded-lg p-5 flex items-center gap-5">
              <div className="bg-white p-2 rounded-md shrink-0">
                <img
                  src={SITE.googlePartner}
                  alt="Google Partner"
                  className="h-12 w-12 object-contain"
                />
              </div>
              <p className="text-sm text-white/75 leading-relaxed">
                <strong className="text-white">Somos Google Partner</strong>, o
                mais alto nível de parceria com o Google — selo de excelência
                em gestão de campanhas e resultados comprovados.
              </p>
            </div>
          </div>

          {/* RIGHT — Bento 2x2 */}
          <div
            className="lg:col-span-5 grid grid-cols-2 gap-4 reveal"
            style={{ animationDelay: ".15s" }}
          >
            {/* Card 1 — dark */}
            <div
              data-testid="hero-stat-anos"
              className="aspect-square border border-white/10 bg-white/[0.04] rounded-2xl p-6 sm:p-8 flex flex-col justify-between hover:bg-white/[0.07] transition"
            >
              <Calendar size={22} className="text-white/40" />
              <div>
                <p className="big-number text-5xl sm:text-6xl text-white">
                  +10
                </p>
                <p className="mt-2 text-sm text-white/60 leading-tight">
                  Anos de Experiência
                </p>
              </div>
            </div>

            {/* Card 2 — google blue */}
            <div
              data-testid="hero-stat-clientes"
              className="aspect-square rounded-2xl p-6 sm:p-8 flex flex-col justify-between transition hover:-translate-y-1"
              style={{ background: "#4285F4" }}
            >
              <Users size={22} className="text-white/90" />
              <div>
                <p className="big-number text-5xl sm:text-6xl text-white">
                  +100
                </p>
                <p className="mt-2 text-sm text-white/90 leading-tight">
                  Clientes Atendidos
                </p>
              </div>
            </div>

            {/* Card 3 — google red */}
            <div
              data-testid="hero-stat-roi"
              className="aspect-square rounded-2xl p-6 sm:p-8 flex flex-col justify-between transition hover:-translate-y-1"
              style={{ background: "#EA4335" }}
            >
              <Target size={22} className="text-white/90" />
              <div>
                <p className="big-number text-4xl sm:text-5xl text-white">
                  R$ 5MM
                </p>
                <p className="mt-2 text-sm text-white/90 leading-tight">
                  Em Investimentos Gerenciados
                </p>
              </div>
            </div>

            {/* Card 4 — dark */}
            <div
              data-testid="hero-stat-suporte"
              className="aspect-square border border-white/10 bg-white/[0.04] rounded-2xl p-6 sm:p-8 flex flex-col justify-between hover:bg-white/[0.07] transition"
            >
              <Star size={22} className="text-[#FBBC05]" />
              <div>
                <p className="big-number text-5xl sm:text-6xl text-white">
                  ROI
                </p>
                <p className="mt-2 text-sm text-white/60 leading-tight">
                  Foco em Resultados
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Google color stripe to transition into white sections */}
        <div className="g-stripe" />
      </section>

      {/* GOOGLE PARTNER SECTION */}
      <Section testId="google-partner-section" className="bg-[#F8F9FA] border-b border-gray-100">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="bg-white border border-gray-100 p-12 flex flex-col items-center text-center">
              <img
                src={SITE.googlePartner}
                alt="Google Partner Badge"
                className="w-48 h-auto"
              />
              <p className="mt-6 eyebrow">Certificação Oficial</p>
            </div>
          </div>
          <div className="lg:col-span-7">
            <Eyebrow color="#4285F4">Reconhecimento Oficial</Eyebrow>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-[#000000] mt-4">
              Somos <span className="g-underline">Google Partner</span> — o mais
              alto nível de parceria do Google.
            </h2>
            <p className="mt-6 text-[#000000]/70 leading-relaxed">
              Esse selo demonstra excelência na gestão de campanhas, domínio
              técnico da plataforma e compromisso com resultados reais para
              nossos clientes.
            </p>
            <p className="mt-4 text-[#000000]/70 leading-relaxed">
              Nossa equipe trabalha diariamente utilizando estratégias
              avançadas, certificações atualizadas e acesso direto a recursos
              exclusivos do Google para gerar o melhor retorno possível sobre
              cada investimento realizado.
            </p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { c: "#4285F4", t: "Especialistas Certificados" },
                { c: "#EA4335", t: "Suporte Premium Google" },
                { c: "#FBBC05", t: "Acesso a Recursos Beta" },
                { c: "#34A853", t: "Treinamento Contínuo" },
              ].map((it) => (
                <div key={it.t} className="border-t-2 pt-3" style={{ borderColor: it.c }}>
                  <p className="text-xs font-medium text-[#000000] leading-tight">
                    {it.t}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* QUEM SOMOS */}
      <Section testId="quem-somos-section">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Eyebrow>Quem Somos</Eyebrow>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-[#000000] mt-4">
              Especialistas em crescimento digital.
            </h2>
            <p className="mt-6 text-[#000000]/70 leading-relaxed">
              A O.G. Marketing Digital atua há mais de 10 anos desenvolvendo
              estratégias digitais para empresas que desejam aumentar sua
              presença online, gerar mais oportunidades de negócio e crescer de
              forma previsível.
            </p>
            <p className="mt-4 text-[#000000]/70 leading-relaxed">
              Nosso foco não é apenas gerar tráfego — criamos estratégias que
              realmente aumentam o faturamento dos nossos clientes.
            </p>
            <Link
              to="/contato"
              data-testid="quem-somos-cta"
              className="btn-outline mt-8"
            >
              Conhecer melhor
              <ArrowUpRight size={18} />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-px bg-gray-100 border border-gray-100">
            {numbers.map((n, i) => (
              <div
                key={n.label}
                className="bg-white p-8 sm:p-10 relative"
                data-testid={`number-card-${i}`}
              >
                <div
                  className="absolute top-0 left-0 w-10 h-[3px]"
                  style={{ background: n.color }}
                />
                <p className="big-number text-5xl sm:text-6xl text-[#000000]">
                  {n.value}
                </p>
                <p className="mt-3 text-sm text-[#000000]/60 leading-tight">
                  {n.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* SERVIÇOS */}
      <Section testId="services-section" className="bg-[#F8F9FA] border-y border-gray-100">
        <SectionHeading
          eyebrow="Nossos Serviços"
          title="O que fazemos pela sua empresa."
          subtitle="Três frentes de crescimento integradas — cada uma trabalhada por especialistas dedicados."
        />
        <div className="grid md:grid-cols-3 gap-6">
          {services.map((s) => {
            const Icon = s.icon;
            return (
              <Link
                to={s.href}
                key={s.title}
                data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                className={`og-card ${s.topClass} block group`}
              >
                <Icon size={28} style={{ color: s.color }} />
                <h3 className="font-display text-2xl font-bold text-[#000000] mt-6">
                  {s.title}
                </h3>
                <p className="mt-3 text-[#000000]/70 leading-relaxed text-sm">
                  {s.desc}
                </p>
                <span className="inline-flex items-center gap-2 mt-6 text-sm font-medium text-[#000000] group-hover:gap-3 transition-all">
                  Saiba mais
                  <ArrowUpRight size={16} />
                </span>
              </Link>
            );
          })}
        </div>
      </Section>

      {/* DIFERENCIAIS */}
      <Section testId="differentials-section">
        <SectionHeading
          eyebrow="Diferenciais"
          title="Por que empresas escolhem a O.G."
          subtitle="Mais que uma agência, somos um time de performance focado no seu crescimento."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-100 border border-gray-100">
          {differentials.map((d) => {
            const Icon = d.icon;
            return (
              <div
                key={d.title}
                className="bg-white p-8 hover:bg-[#F8F9FA] transition-colors"
                data-testid={`differential-${d.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <Icon size={22} style={{ color: d.color }} />
                  <span
                    className="h-px flex-1"
                    style={{ background: d.color, opacity: 0.3 }}
                  />
                </div>
                <h3 className="font-display text-xl font-bold text-[#000000]">
                  {d.title}
                </h3>
                <p className="mt-3 text-[#000000]/70 leading-relaxed text-sm">
                  {d.desc}
                </p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* TESTIMONIALS */}
      <Section testId="testimonials-section" className="bg-[#F8F9FA] border-y border-gray-100">
        <SectionHeading
          eyebrow="Avaliações"
          title="O que dizem nossos clientes."
          subtitle="A confiança de mais de 100 empresas que crescem com a O.G."
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {testimonials.map((t, i) => (
            <div
              key={t.name}
              data-testid={`testimonial-${i}`}
              className="og-card bg-white"
            >
              <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star
                    key={s}
                    size={14}
                    className="fill-[#FBBC05] text-[#FBBC05]"
                  />
                ))}
              </div>
              <p className="text-[#000000]/80 leading-relaxed text-sm">
                {`"${t.text}"`}
              </p>
              <div className="mt-6 pt-4 border-t border-gray-100">
                <p className="font-display font-bold text-[#000000]">{t.name}</p>
                <p className="text-xs text-[#000000]/60 mt-1">{t.role}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link
            to="/avaliacoes"
            data-testid="see-all-testimonials"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#000000] hover:gap-3 transition-all"
          >
            Ver todas as avaliações
            <ArrowUpRight size={16} />
          </Link>
        </div>
      </Section>

      {/* FAQ */}
      <Section testId="home-faq" className="border-t border-gray-100">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Eyebrow>Perguntas Frequentes</Eyebrow>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-[#000000] mt-4 leading-[1.05]">
              Tudo o que você precisa saber{" "}
              <span className="g-underline">antes</span> de começar.
            </h2>
            <p className="mt-6 text-[#000000]/70 leading-relaxed">
              Reunimos as dúvidas mais comuns de quem está pesquisando uma
              agência. Não encontrou sua pergunta? Fale com nosso time pelo
              WhatsApp — respondemos em minutos.
            </p>
            <a
              href={waUrl("Olá! Tenho uma dúvida sobre os serviços da O.G.")}
              target="_blank"
              rel="noreferrer"
              data-testid="home-faq-whatsapp"
              className="btn-whatsapp mt-8 inline-flex"
            >
              <MessageCircle size={18} />
              Tirar minhas dúvidas
            </a>
          </div>
          <div className="lg:col-span-7">
            <Accordion
              type="single"
              collapsible
              className="border border-gray-200"
              data-testid="home-faq-accordion"
              defaultValue="item-0"
            >
              {homeFaqs.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`item-${i}`}
                  className="px-6 border-b border-gray-200 last:border-b-0"
                >
                  <AccordionTrigger
                    data-testid={`home-faq-trigger-${i}`}
                    className="text-left font-display font-semibold text-[#000000] hover:no-underline py-5 text-base sm:text-lg"
                  >
                    <span className="flex items-center gap-4">
                      <span
                        className="text-xs font-mono tracking-widest"
                        style={{ color: ["#4285F4", "#EA4335", "#FBBC05", "#34A853"][i % 4] }}
                      >
                        0{i + 1}
                      </span>
                      {f.q}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent
                    data-testid={`home-faq-content-${i}`}
                    className="text-[#000000]/70 leading-relaxed pb-5 pl-10"
                  >
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </Section>

      <CTABand
        title="Pronto para crescer junto com a O.G.?"
        subtitle="Solicite uma análise gratuita do seu negócio e descubra quanto sua empresa pode crescer nos próximos 90 dias."
      />
    </div>
  );
}
